import { SHORTCUTS } from '../laws'

/**
 * The ⌘K palette, the ? overlay and the toast.
 *
 * Verbatim from `docs/design/reference/template.html` lines 754–819.
 *
 * The palette's footer states a real constraint rather than a hint: money-
 * shaped actions are not reachable from here. Posting a correction or
 * restricting a customer needs its screen's context — the journal row, the
 * customer — in view before it can be authorised, and an action fired from a
 * search box is an action fired without the thing it acts on on screen.
 */

export interface PaletteHit {
  readonly label: string
  readonly match: string
  readonly meta: string
  readonly dim: boolean
  readonly go: () => void
}

export interface PaletteGroup {
  readonly label: string
  readonly items: readonly PaletteHit[]
}

interface PaletteProps {
  readonly query: string
  readonly onQuery: (value: string) => void
  readonly groups: readonly PaletteGroup[]
  readonly noMatch: boolean
  readonly onClose: () => void
}

export function Palette({ query, onQuery, groups, noMatch, onClose }: PaletteProps) {
  const commandMode = query.trim().startsWith('>')

  return (
    <Scrim onClose={onClose} zIndex={50} align="flex-start" paddingTop="9vh">
      <div
        style={{
          width: 'min(760px,92vw)',
          background: 'var(--k-panel)',
          border: '2px solid var(--k-rule)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '74vh',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            padding: '0 12px',
            height: '44px',
            borderBottom: '2px solid var(--k-rule)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--k-mono)',
              fontSize: '12px',
              color: 'var(--k-accent)',
              fontWeight: 600,
            }}
          >
            {commandMode ? '>' : '/'}
          </span>
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Search entities, or type > for commands"
            aria-label="Search entities and commands"
            style={{
              flex: 1,
              height: '30px',
              border: 0,
              background: 'transparent',
              color: 'var(--k-ink)',
              font: 'inherit',
              fontSize: '15px',
              outline: 'none',
            }}
          />
          <span style={{ fontFamily: 'var(--k-mono)', fontSize: '10.5px', color: 'var(--k-mut2)' }}>
            esc
          </span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {groups.map((g) => (
            <div key={g.label}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 12px 3px',
                  fontSize: '9.5px',
                  letterSpacing: '.09em',
                  textTransform: 'uppercase',
                  color: 'var(--k-mut2)',
                }}
              >
                <span>{g.label}</span>
                <span style={{ fontFamily: 'var(--k-mono)' }}>{g.items.length}</span>
              </div>
              {g.items.map((i) => (
                <button
                  key={`${g.label}-${i.label}`}
                  type="button"
                  className="k-hov"
                  onClick={i.go}
                  data-dim={i.dim ? '1' : ''}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '6px 12px',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--k-ink)',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      flex: '0 0 auto',
                      width: '5px',
                      height: '5px',
                      background: 'var(--k-accent)',
                    }}
                  />
                  <span style={{ fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {i.label}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--k-mono)',
                      fontSize: '11px',
                      color: 'var(--k-mut)',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {i.match}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      flex: '0 0 auto',
                      fontSize: '10.5px',
                      color: 'var(--k-mut2)',
                      letterSpacing: '.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {i.meta}
                  </span>
                </button>
              ))}
            </div>
          ))}

          {/* The empty state names what was searched, which since #30 is only
              screens (or, behind `>`, only commands). It used to read "Nothing
              in customers, IBANs, provider references, postings, decision_refs
              … matches that string" — and with the Entities group gone that
              sentence asserts a lookup nobody performed. An operator pasting an
              IBAN would have been told their customer's account is not in this
              system, on the strength of a search that never ran.

              It is keyed off `commandMode` because the two searches are
              disjoint: outside `>` the palette matches screen names only, so
              "no screen or command matches" was itself a false negative —
              "Post correction" is in COMMANDS, does match, and is one keystroke
              away. A sentence naming what ran has to be right about which one
              ran. */}
          {noMatch && (
            <div style={{ padding: '22px 14px' }}>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {commandMode ? 'No command matches' : 'No screen matches'}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--k-mut)',
                  marginTop: '4px',
                  maxWidth: '64ch',
                  textWrap: 'pretty',
                }}
              >
                {commandMode
                  ? 'The console’s own commands are all that were searched — nothing else is ' +
                    'reachable behind the > prefix. Delete it to search screen names instead.'
                  : 'Screen names are all that were searched; commands are searched with a > ' +
                    'prefix. Identifier lookup — customers, IBANs, provider references, postings, ' +
                    'decision_refs, idempotency keys, cases, audit events — is not wired yet: ' +
                    'there is no admin search endpoint to ask. So this is not a finding that the ' +
                    'identifier is absent. Nobody looked.'}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '7px 12px',
            borderTop: '1px solid var(--k-line)',
            background: 'var(--k-bg)',
            fontSize: '10.5px',
            color: 'var(--k-mut2)',
            fontFamily: 'var(--k-mono)',
          }}
        >
          <span>↑↓ move · ↵ open · esc close</span>
          <span style={{ marginLeft: 'auto' }}>
            Money-shaped actions are not reachable here — they need their screen and its context
          </span>
        </div>
      </div>
    </Scrim>
  )
}

export function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <Scrim onClose={onClose} zIndex={60} align="center">
      <div
        style={{
          width: 'min(620px,92vw)',
          background: 'var(--k-panel)',
          border: '2px solid var(--k-rule)',
          padding: '18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '10px',
            borderBottom: '2px solid var(--k-rule)',
            paddingBottom: '8px',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '18px' }}>Keyboard</h3>
          <span style={{ fontSize: '11.5px', color: 'var(--k-mut)' }}>
            Queues are workable without the mouse
          </span>
          <span
            style={{
              marginLeft: 'auto',
              fontFamily: 'var(--k-mono)',
              fontSize: '11px',
              color: 'var(--k-mut2)',
            }}
          >
            ? to close
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))',
            gap: '2px 20px',
            marginTop: '12px',
          }}
        >
          {SHORTCUTS.map(([keys, does]) => (
            <div
              key={keys}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '4px 0',
                borderBottom: '1px solid var(--k-line)',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--k-mono)',
                  fontSize: '11px',
                  border: '1px solid var(--k-line)',
                  padding: '1px 5px',
                  minWidth: '34px',
                  textAlign: 'center',
                }}
              >
                {keys}
              </span>
              <span style={{ fontSize: '12px' }}>{does}</span>
            </div>
          ))}
        </div>
      </div>
    </Scrim>
  )
}

export function Toast({ message }: { message: string }) {
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: '14px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 70,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 12px',
        background: 'var(--k-ink)',
        color: 'var(--k-bg)',
        fontSize: '12px',
        animation: 'kfd .1s ease-out',
      }}
    >
      <span style={{ width: '6px', height: '6px', background: 'var(--k-accent)' }} />
      <span>{message}</span>
    </div>
  )
}

/**
 * The shared modal backdrop. Clicking it closes; clicking through to the panel
 * does not. Escape is handled once, globally, in Console.
 */
function Scrim({
  onClose,
  zIndex,
  align,
  paddingTop,
  children,
}: {
  onClose: () => void
  zIndex: number
  align: 'center' | 'flex-start'
  paddingTop?: string
  children: React.ReactNode
}) {
  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'color-mix(in srgb,#000 46%,transparent)',
        zIndex,
        display: 'flex',
        alignItems: align,
        justifyContent: 'center',
        paddingTop: paddingTop ?? '0',
        animation: 'kfd .1s ease-out',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} role="presentation">
        {children}
      </div>
    </div>
  )
}
